import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildPointsKey from 'loyalty/lib/pointsCalculation/buildPointsKey'
import { nonNullable, sum } from 'lib/array/arrayUtils'
import { getPointsCalculationsByKeys } from 'loyalty/selectors/pointsCalculation'
import submitLoyaltyPointsEarnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsEarnCalculationRequest'
import submitLoyaltyPointsEarnCalculationBatchRequest from 'actions/loyalty/submitLoyaltyPointsEarnCalculationBatchRequest'
import { getAgentHubCommissionMap } from 'agentHub/selectors/agentHubSelectors'
import { isLuxLoyaltyEnabledInAgentHub } from 'agentHub/selectors/isLuxLoyaltyEnabledInAgentHub'
import { getCalculationRequestAfterCommissionIfAgentHubEnabled } from 'lib/agentHub/agentHubUtils'

const INITIAL_LUX_LOYALTY_POINTS_EARN_CALCULATION_STATE: App.StatefulData<App.LoyaltyPointsEarnCalculation, boolean> = {
  initial: true,
}

interface TotalPointsCalculation {
  totalPointsCalculation: App.StatefulData<Omit<App.LoyaltyPointsEarnCalculation, 'request'>, boolean>
  /** @dev primarily used for debugging individual points calculations */
  pointsCalculations: Array<App.StatefulData<App.LoyaltyPointsEarnCalculation> | undefined>
}

interface Options {
  skipAll?: boolean
  /** When true, sends all requests in a single batch API call. @default false */
  useBatch?: boolean
}

function useLoyaltyPointsEarnCalculator(
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined,
  options?: Options,
): TotalPointsCalculation {
  const dispatch = useAppDispatch()
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const { skipAll, useBatch = false } = options ?? {}

  const agentHubCommissionMap = useAppSelector(getAgentHubCommissionMap)

  const requestsToCalculate = useMemo(() => {
    if (skipAll) return []
    const requests = Array.isArray(calculationRequests) ? calculationRequests : [calculationRequests]

    // agent hub does not want to send any requests to loyalty until every commission has been fetched
    if (isLuxLoyaltyEnabledInAgentHub) {
      const offerIds = new Set(nonNullable(requests.map((calculationRequest) => calculationRequest?.offerId)))
      const hasAllCommissionsMapped = Array.from(offerIds).every((offerId) => agentHubCommissionMap[offerId || ''])
      if (!hasAllCommissionsMapped) return []
    }

    const newRequests = requests.map((calculationRequest) => {
      if (!calculationRequest) return undefined
      return getCalculationRequestAfterCommissionIfAgentHubEnabled(calculationRequest, agentHubCommissionMap)
    })
    return nonNullable(newRequests)
    // Please keep the customerId and currency as dependencies (see related action)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationRequests, skipAll, customerId, currency, agentHubCommissionMap])

  useEffect(() => {
    if (requestsToCalculate.length === 0) return

    if (useBatch) {
      dispatch(submitLoyaltyPointsEarnCalculationBatchRequest(requestsToCalculate))
    } else {
      requestsToCalculate.forEach((calculationRequest) => {
        dispatch(submitLoyaltyPointsEarnCalculationRequest(calculationRequest))
      })
    }
  }, [dispatch, requestsToCalculate, agentHubCommissionMap, useBatch])

  const pointsKeys = useMemo(() => requestsToCalculate.map((calculationRequest) => buildPointsKey(calculationRequest, customerId)), [requestsToCalculate, customerId])
  const pointsCalculations = useAppSelector(state => getPointsCalculationsByKeys(state, pointsKeys))

  return useMemo((): TotalPointsCalculation => {
    // A request whose key has never been dispatched yet (e.g. a ticket count just
    // changed the price) has no entry in the store at all - not `initial`, not
    // `fetching`. Treat that the same as fetching, otherwise it gets summed as 0
    // and briefly reported as a resolved result.
    const anyMissing = pointsCalculations.some(pointsCalculation => pointsCalculation === undefined)
    const isInitial = !!pointsCalculations.every(pointsCalculation => !!pointsCalculation?.initial)
    const anyError = !!pointsCalculations.some(pointsCalculation => !!pointsCalculation?.error)
    const anyFetching = anyMissing || pointsCalculations.some(pointsCalculation => !!pointsCalculation?.fetching)

    if (isInitial) {
      return {
        totalPointsCalculation: INITIAL_LUX_LOYALTY_POINTS_EARN_CALCULATION_STATE,
        pointsCalculations,
      }
    }

    if (anyFetching) {
      return {
        totalPointsCalculation: {
          fetching: true,
        },
        pointsCalculations,
      }
    }

    if (anyError) {
      return {
        totalPointsCalculation: {
          error: true,
          fetching: false,
        },
        pointsCalculations,
      }
    }
    const basePoints = sum(pointsCalculations, calculation => calculation?.data?.basePoints ?? 0)
    const bonusPoints = sum(pointsCalculations, calculation => calculation?.data?.bonusPoints ?? 0)
    const bonusStatusCredits = sum(pointsCalculations, calculation => calculation?.data?.bonusStatusCredits ?? 0)
    const statusCredits = sum(pointsCalculations, calculation => calculation?.data?.statusCredits ?? 0)
    const allAppliedRuleIds = pointsCalculations.flatMap(calculation => calculation?.data?.appliedRuleIds ?? [])
    const allCustomRulesBreakdown = pointsCalculations.flatMap(calculation => calculation?.data?.customRulesBreakdown ?? [])
    const allEarnDecisionsBreakdown = pointsCalculations.flatMap(calculation => calculation?.data?.earnDecisionsBreakdown ?? [])

    return {
      totalPointsCalculation: {
        fetching: false,
        data: {
          points: basePoints + bonusPoints,
          basePoints,
          bonusPoints,
          bonusStatusCredits,
          statusCredits,
          // If any calculation has rounding disabled, then we should use that for the total
          roundingEnabled: !pointsCalculations.some(calculation => !calculation?.data?.roundingEnabled),
          roundedPointsEarnRatio: sum(pointsCalculations, calculation => calculation?.data?.roundedPointsEarnRatio ?? 0),
          appliedRuleIds: allAppliedRuleIds,
          customRulesBreakdown: allCustomRulesBreakdown,
          earnDecisionsBreakdown: allEarnDecisionsBreakdown,
        },
      },
      pointsCalculations,
    }
  }, [pointsCalculations])
}

export default useLoyaltyPointsEarnCalculator
